// @ts-nocheck
import { createTheme } from '@mui/material/styles';

const modernDialogOverrides = {
  MuiDialog: {
    defaultProps: {
      BackdropProps: {
        sx: {
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
        },
      },
    },
    styleOverrides: {
      paper: {
        borderRadius: 20,
        boxShadow: '0 10px 40px -10px rgba(0,0,0,0.2)',
        backgroundImage: 'none',
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        padding: '24px 32px 16px',
        fontSize: '1.5rem',
        fontWeight: 700,
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: '16px 32px 24px',
        paddingTop: '0 !important', // Prevent accumulation with title
      },
      dividers: {
        borderTop: 'none',
        borderBottom: 'none',
      }
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '16px 32px 24px',
        justifyContent: 'flex-end',
        '& .MuiButton-root': {
          borderRadius: 12,
          padding: '8px 20px',
          textTransform: 'none',
          fontSize: '0.95rem',
          fontWeight: 400,
        },
      },
    },
  },
};

// Light theme settings
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#061ab1',
    },
    background: {
      default: '#fafafa', // Light mode background color
    },
    // Additional customizations here
  },
  components: {
    ...modernDialogOverrides,
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#061ab1', // Changed to blue for light mode
          '.MuiTableCell-root': {
            color: '#ffffff', // Setting table header text color to white in light mode
          },
        },
      },
    },
    // Customization for Link component in light mode to keep the current color
    MuiLink: {
      styleOverrides: {
        root: {
          color: 'inherit', // Keeps the Link color as is in light mode
        },
      },
    },
  },
});

// Dark theme settings
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#061ab1', // Changed back to original blue color
      dark: '#051593', // Slightly darker blue for hover states
      contrastText: '#ffffff', // White text for better contrast on blue
    },
    background: {
      default: '#121212', // Dark mode background color
      paper: '#1e1e1e',
    },
    // Additional customizations here
  },
  // Change the drawer's background color in dark mode
  components: {
    ...modernDialogOverrides,
    MuiDialog: {
      defaultProps: modernDialogOverrides.MuiDialog.defaultProps,
      styleOverrides: {
        paper: {
          ...modernDialogOverrides.MuiDialog.styleOverrides.paper,
          backgroundColor: '#1e1e1e',
          border: '1px solid rgba(255, 255, 255, 0.08)',
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#363636', // Setting drawer background color for dark mode
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#363636', // Changed to gray for dark mode
          '.MuiTableCell-root': {
            color: '#ffffff', // Ensuring table header text remains white in dark mode
          },
        },
      },
    },
    // Customization for Link component in dark mode to make hyperlinks white
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#ffffff', // Sets the Link color to white in dark mode
        },
      },
    },
    // Add button customization for dark mode
    MuiButton: {
      styleOverrides: {
        root: {
          // Only apply these styles to buttons that don't have specific colors like error, warning, etc.
          '&.MuiButton-contained:not(.MuiButton-containedError):not(.MuiButton-containedWarning):not(.MuiButton-containedSuccess):not(.MuiButton-containedInfo)': {
            backgroundColor: '#061ab1',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#051593',
            },
          },
          '&.MuiButton-outlined:not(.MuiButton-outlinedError):not(.MuiButton-outlinedWarning):not(.MuiButton-outlinedSuccess):not(.MuiButton-outlinedInfo)': {
            borderColor: '#061ab1',
            color: '#ffffff',
            '&:hover': {
              borderColor: '#051593',
              color: '#ffffff',
            },
          },
          '&.MuiButton-text:not(.MuiButton-textError):not(.MuiButton-textWarning):not(.MuiButton-textSuccess):not(.MuiButton-textInfo)': {
            color: '#061ab1',
            '&:hover': {
              color: '#051593',
            },
          },
        },
        // Ensure that buttons with color="inherit" maintain their styling
        containedInherit: {
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
          color: '#fff',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
          },
        },
      },
    },
    // Add Paper component customization for SectionHeader in dark mode
    MuiPaper: {
      styleOverrides: {
        root: {
          '&.MuiPaper-root[class*="SectionHeader"]': {
            color: '#ffffff', // Ensure text in SectionHeader is white in dark mode
          }
        }
      }
    },
    // Add styling for icons in card titles
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          // Target icons in card titles, headers, and other prominent UI elements
          '.MuiCardHeader-avatar &, .MuiCardHeader-action &, h1 &, h2 &, h3 &, h4 &, h5 &, h6 &, .MuiTypography-root &': {
            color: '#061ab1', // Use the original blue color for icons
          }
        }
      }
    },
    // Make IconButton color white in dark mode (for close buttons)
    MuiIconButton: {
      styleOverrides: {
        root: {
          // Target all icon buttons in dark mode
          color: '#ffffff', // Make all icon buttons white in dark mode
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)', // Subtle hover effect
          },
          // Specifically target close buttons in dialogs and modals
          '.MuiDialogTitle-root &[aria-label="close"], .MuiDialog-root &[aria-label="close"], .MuiModal-root &[aria-label="close"]': {
            color: '#ffffff !important', // Force white color with !important
          },
          // Target any IconButton that contains CloseIcon
          '& .MuiSvgIcon-root[data-testid="CloseIcon"]': {
            color: '#ffffff !important', // Force white color with !important
          },
          // Target IconButtons with color="inherit" in dark mode
          '&.MuiIconButton-colorInherit': {
            color: '#ffffff !important', // Force white color with !important
          },
        },
        // Override the colorInherit variant specifically
        colorInherit: {
          color: '#ffffff !important', // Force white color with !important
        },
      },
    },
    // Make Dialog action buttons white in dark mode
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 32px 24px',
          justifyContent: 'flex-end',
          '& .MuiButton-text': {
            color: '#ffffff !important', // Make text buttons (like "Close") white in dark mode with !important
          },
          // Target all buttons in dialog actions
          '& .MuiButton-root': {
            color: '#ffffff !important', // Force white color with !important for all buttons in dialog actions
            borderRadius: 12,
            padding: '8px 20px',
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '0.95rem',
          },
        },
      },
    },
  },
  // Other theme settings
});

export { lightTheme, darkTheme };